import { isNullOrUndefined, getValue, setValue } from '@syncfusion/ej2-base';
import { ConstraintType } from './enum';
/**
 *  Date processor is used to handle date of task data.
 */
export class DateProcessor {
    constructor(parent) {
        this.fromSegments = false;
        this.mondayTimeRangeLength = 0;
        this.tuesdayTimeRangeLength = 0;
        this.wednesdayTimeRangeLength = 0;
        this.thursdayTimeRangeLength = 0;
        this.fridayTimeRangeLength = 0;
        this.saturdayTimeRangeLength = 0;
        this.sundayTimeRangeLength = 0;
        this.parent = parent;
    }
    /**
     * @param {ITaskData} ganttProp .
     * @returns {boolean} .
     */
    isValidateNonWorkDays(ganttProp) {
        return (!isNullOrUndefined(ganttProp) && ganttProp.isAutoSchedule &&
            ((!this.parent.includeWeekend ||
                ganttProp.calendarContext.defaultHolidays.length > 0))) ||
            (isNullOrUndefined(ganttProp) &&
                (!this.parent.includeWeekend ||
                    this.parent.defaultCalendarContext.defaultHolidays.length > 0));
    }
    /**
     * Method to convert given date value as valid start date
     *
     * @param {Date} date .
     * @param {ITaskData} ganttProp - The Gantt properties related to the task.
     * @param {boolean} validateAsMilestone - Indicates whether the date should be validated as a milestone.
     * @param {boolean} isLoad - A flag indicating if the method is called during the loading process.
     * @param {boolean} isBaseline - Indicates whether the calculation is specific to baseline dates.
     * @param {boolean} ignoreAutoSchedule - Indicates whether to consider the autoschedule or not.
     * @returns {Date} .
     * @private
     */
    checkStartDate(date, ganttProp, validateAsMilestone, isLoad, isBaseline, ignoreAutoSchedule) {
        if (isNullOrUndefined(date)) {
            return null;
        }
        const currentDay = new Date(date.getTime());
        let dayStartTime = this.parent['getCurrentDayStartTime'](currentDay);
        let dayEndTime = this.parent['getCurrentDayEndTime'](currentDay);
        let cloneStartDate = new Date(date.getTime());
        const hour = this.getSecondsInDecimal(cloneStartDate);
        validateAsMilestone = isNullOrUndefined(validateAsMilestone) ? !isNullOrUndefined(ganttProp) ?
            ganttProp.isMilestone : false : validateAsMilestone;
        if (hour < dayStartTime && (!validateAsMilestone || isLoad)) {
            this.setTime(dayStartTime, cloneStartDate);
        }
        else if (hour < dayStartTime && validateAsMilestone) {
            this.setTime(dayStartTime, cloneStartDate);
        }
        else if ((hour === dayEndTime && (!ganttProp || !validateAsMilestone)) || hour > dayEndTime) {
            cloneStartDate.setDate(cloneStartDate.getDate() + 1);
            dayStartTime = this.parent['getCurrentDayStartTime'](cloneStartDate);
            this.setTime(dayStartTime, cloneStartDate);
        }
        else if (hour > dayStartTime && hour < dayEndTime) {
            let workingRange = this.parent.workingTimeRanges;
            if (this.parent.weekWorkingTime.length > 0) {
                workingRange = this.parent['getWorkingRange'](cloneStartDate);
            }
            for (let index = 0; index < workingRange.length; index++) {
                const value = workingRange[index];
                if (hour >= value.to && (workingRange[index + 1] &&
                    hour < workingRange[index + 1].from)) {
                    // milestone can fall at end any interval time
                    if ((hour === value.to && (!ganttProp || !validateAsMilestone)) || hour !== value.to) {
                        this.setTime(workingRange[index + 1].from, cloneStartDate);
                    }
                    break;
                }
            }
        }
        let tStartDate;
        if (this.parent.autoCalculateDateScheduling && !ignoreAutoSchedule && !(this.parent.isLoad &&
            this.parent.treeGrid.loadChildOnDemand && this.parent.taskFields.hasChildMapping)) {
            do {
                tStartDate = new Date(cloneStartDate.getTime());
                if (ganttProp && !ganttProp.calendarContext) {
                    ganttProp.calendarContext = this.parent.defaultCalendarContext;
                }
                const calendarContext = ganttProp
                    ? ganttProp.calendarContext
                    : this.parent.defaultCalendarContext;
                const holidayLength = calendarContext.defaultHolidays.length;
                // check holidays and weekends
                if (this.isValidateNonWorkDays(ganttProp)) {
                    dayStartTime = this.parent['getCurrentDayStartTime'](tStartDate);
                    if (ganttProp) {
                        if (!isBaseline) {
                            dayEndTime = this.parent['getCurrentDayEndTime'](ganttProp.endDate ? ganttProp.isAutoSchedule ? ganttProp.endDate : ganttProp.autoEndDate : tStartDate);
                        }
                        else {
                            dayEndTime = this.parent['getCurrentDayEndTime'](ganttProp.baselineEndDate ? ganttProp.baselineEndDate : tStartDate);
                        }
                    }
                    let startTime = (!validateAsMilestone || isLoad) ? dayStartTime : dayEndTime;
                    if (!this.parent.includeWeekend && !isBaseline) {
                        const tempDate = new Date(cloneStartDate.getTime());
                        cloneStartDate = this.getNextWorkingDay(cloneStartDate, calendarContext);
                        startTime = this.parent['getCurrentDayStartTime'](cloneStartDate);
                        if (tempDate.getTime() !== cloneStartDate.getTime() && !validateAsMilestone) {
                            this.setTime(startTime, cloneStartDate);
                        }
                    }
                    if (!isBaseline) {
                        for (let count = 0; count < holidayLength; count++) {
                            const holidayFrom = this.getDateFromFormat(new Date(calendarContext.defaultHolidays[count]));
                            const holidayTo = new Date(holidayFrom.getTime());
                            holidayFrom.setHours(0, 0, 0, 0);
                            holidayTo.setHours(23, 59, 59, 59);
                            if (cloneStartDate.getTime() >= holidayFrom.getTime() && cloneStartDate.getTime() < holidayTo.getTime()) {
                                cloneStartDate.setDate(cloneStartDate.getDate() + 1);
                                startTime = this.parent['getCurrentDayStartTime'](cloneStartDate);
                                this.setTime(startTime, cloneStartDate);
                            }
                        }
                    }
                }
            } while (tStartDate.getTime() !== cloneStartDate.getTime());
            return new Date(cloneStartDate.getTime());
        }
        else {
            return new Date(cloneStartDate.getTime());
        }
    }
    getAdjustedStartDate(constraintDate, ganttProp) {
        const adjustedDate = new Date(constraintDate);
        adjustedDate.setDate(adjustedDate.getDate() + 1);
        const checkedEnd = this.parent.dateValidationModule.checkEndDate(adjustedDate);
        return this.parent.dateValidationModule.getStartDate(checkedEnd, ganttProp.duration, ganttProp.durationUnit, ganttProp);
    }
    getDateByConstraint(ganttData, date, validPredecessor) {
        const ganttProp = ganttData['ganttProperties'] ? ganttData['ganttProperties'] : ganttData;
        let constraintDate = new Date(ganttProp.constraintDate);
        const isLoad = this.parent.isLoad;
        if (isNullOrUndefined(validPredecessor)) {
            validPredecessor = true;
        }
        if (validPredecessor && (!constraintDate || !date)) {
            return null;
        }
        if (this.parent.editModule && this.parent.editModule.dialogModule && this.parent.editModule.dialogModule['dialogConstraintDate']) {
            constraintDate = new Date(this.parent.editModule.dialogModule['dialogConstraintDate']);
        }
        else {
            constraintDate = this.parent['assignTimeToDate'](constraintDate, this.parent['getCurrentDayStartTime'](constraintDate));
        }
        switch (ganttProp.constraintType) {
            case ConstraintType.AsSoonAsPossible:
                if (isLoad) {
                    return date;
                }
                else {
                    ganttProp.constraintDate = null;
                    let parentRecord = null;
                    const record = this.parent.getRecordByID(ganttProp.taskId);
                    if (record && record.parentItem) {
                        parentRecord = record.parentItem;
                    }
                    if (ganttProp.predecessor && ganttProp.predecessor.length > 0) {
                        return this.parent['assignTimeToDate'](date, this.parent['getCurrentDayStartTime'](date));
                    }
                    else if (parentRecord) {
                        return this.parent.getRecordByID(parentRecord.taskId).ganttProperties.startDate;
                    }
                    else {
                        return this.parent.dateValidationModule.checkStartDate(new Date(this.parent.projectStartDate));
                    }
                }
            case ConstraintType.AsLateAsPossible:
                if (isLoad) {
                    return date;
                }
                else {
                    const parentRecord = this.parent.getRecordByID(ganttProp.taskId);
                    if (parentRecord && parentRecord.parentItem) {
                        const checkedEnd = this.parent.dateValidationModule.checkEndDate(this.parent.getRecordByID(parentRecord.parentItem.taskId).ganttProperties.endDate);
                        const start = this.parent.dateValidationModule.getStartDate(checkedEnd, ganttProp.duration, ganttProp.durationUnit, ganttProp);
                        return start;
                    }
                    else {
                        const checkedEnd = this.parent.dateValidationModule.checkEndDate(new Date(this.parent.projectEndDate));
                        const start = this.parent.dateValidationModule.getStartDate(checkedEnd, ganttProp.duration, ganttProp.durationUnit, ganttProp);
                        return start;
                    }
                }
            case ConstraintType.MustStartOn: {
                const isViolation = constraintDate.getTime() !== ganttProp.startDate.getTime();
                let constraintValue;
                if (this.parent.editModule && this.parent.editModule.dialogModule) {
                    constraintValue = this.parent.editModule.dialogModule['dialogConstraintValue'];
                }
                const isConstraintTypeRestricted = isNullOrUndefined(constraintValue) ||
                    constraintValue === 2 ||
                    constraintValue === 3;
                if (isViolation && !isLoad && isConstraintTypeRestricted) {
                    this.parent.constraintViolationType = 'MustStartOn';
                }
                constraintValue = null;
                if (isLoad) {
                    return this.parent.dateValidationModule.checkStartDate(new Date(constraintDate));
                }
                else {
                    return this.parent['assignTimeToDate'](date, this.parent['getCurrentDayStartTime'](date));
                }
            }
            case ConstraintType.MustFinishOn: {
                const isViolation = constraintDate.getTime() !== ganttProp.endDate.getTime();
                if (isViolation && !isLoad) {
                    this.parent.constraintViolationType = 'MustFinishOn';
                }
                return this.getAdjustedStartDate(constraintDate, ganttProp);
            }
            case ConstraintType.StartNoEarlierThan: {
                if (constraintDate.getTime() < date.getTime() || !isLoad) {
                    return this.parent['assignTimeToDate'](date, this.parent['getCurrentDayStartTime'](date));
                }
                return this.parent.dateValidationModule.checkStartDate(new Date(constraintDate));
            }
            case ConstraintType.StartNoLaterThan: {
                const isViolation = constraintDate.getTime() < date.getTime();
                if (isViolation && !isLoad) {
                    this.parent.constraintViolationType = 'StartNoLaterThan';
                }
                if (!isLoad) {
                    const cellEditModule = this.parent.editModule && this.parent.editModule.cellEditModule;
                    const constraintField = this.parent.taskFields && this.parent.taskFields.constraintDate;
                    if (cellEditModule &&
                        cellEditModule.isCellEdit &&
                        cellEditModule.editedColumn &&
                        isViolation &&
                        cellEditModule.editedColumn.field === constraintField) {
                        return constraintDate;
                    }
                    if (this.parent.editModule && this.parent.editModule.dialogModule && isViolation && this.parent.editModule.dialogModule['dialogConstraintDate']) {
                        return constraintDate;
                    }
                    return this.parent['assignTimeToDate'](date, this.parent['getCurrentDayStartTime'](date));
                }
                if (isLoad && isViolation) {
                    return this.parent.dateValidationModule.checkStartDate(new Date(constraintDate));
                }
                else {
                    return date;
                }
            }
            case ConstraintType.FinishNoEarlierThan: {
                const endDate = this.getEndDate(date, ganttProp.duration, ganttProp.durationUnit, ganttProp, false, false);
                if (endDate.getTime() < constraintDate.getTime()) {
                    return this.getAdjustedStartDate(constraintDate, ganttProp);
                }
                else {
                    return date;
                }
            }
            case ConstraintType.FinishNoLaterThan: {
                const endDate = this.getEndDate(date, ganttProp.duration, ganttProp.durationUnit, ganttProp, false, false);
                const isViolation = constraintDate.getTime() < endDate.getTime();
                if (isViolation && !isLoad) {
                    this.parent.constraintViolationType = 'FinishNoLaterThan';
                }
                if (!isLoad) {
                    if (isViolation) {
                        return this.getAdjustedStartDate(constraintDate, ganttProp);
                    }
                    return this.parent['assignTimeToDate'](date, this.parent['getCurrentDayEndTime'](date));
                }
                if (isViolation) {
                    return this.getAdjustedStartDate(constraintDate, ganttProp);
                }
                else {
                    return date;
                }
            }
            default:
                return date;
        }
    }
    /**
     * To update given date value to valid end date
     *
     * @param {Date} date .
     * @param {ITaskData} ganttProp .
     * @param {boolean} validateAsMilestone .
     * @param {boolean} isBaseline - Indicates whether the calculation is specific to baseline dates.
     * @param {boolean} ignoreAutoSchedule - Indicates whether to consider the autoschedule or not.
     * @returns {Date} .
     * @private
     */
    checkEndDate(date, ganttProp, validateAsMilestone, isBaseline, ignoreAutoSchedule) {
        if (isNullOrUndefined(date)) {
            return null;
        }
        let dayStartTime;
        let dayEndTime;
        if (ganttProp && !ganttProp.calendarContext) {
            ganttProp.calendarContext = this.parent.defaultCalendarContext;
        }
        const calendarContext = ganttProp
            ? ganttProp.calendarContext
            : this.parent.defaultCalendarContext;
        if (this.parent.weekWorkingTime.length > 0) {
            let currentDay = new Date(date.getTime());
            if (((!this.parent.includeWeekend) &&
                ganttProp &&
                ganttProp.isAutoSchedule) ||
                (this.parent.editModule &&
                    this.parent.editModule.taskbarEditModule &&
                    this.parent.editModule.taskbarEditModule.taskBarEditRecord &&
                    !this.parent.editModule.taskbarEditModule.taskBarEditRecord.ganttProperties.isAutoSchedule)) {
                currentDay = this.getNextWorkingDay(currentDay, calendarContext);
            }
            dayStartTime = this.parent['getStartTime'](currentDay);
            dayEndTime = this.parent['getEndTime'](currentDay);
        }
        else {
            dayStartTime = this.parent.defaultStartTime;
            dayEndTime = this.parent.defaultEndTime;
        }
        let cloneEndDate = new Date(date.getTime());
        const hour = this.getSecondsInDecimal(cloneEndDate);
        if (hour > dayEndTime) {
            this.setTime(dayEndTime, cloneEndDate);
        }
        else if (hour <= dayStartTime && dayEndTime !== 86400 && !validateAsMilestone) {
            const taskfields = this.parent.taskFields;
            if (this.parent.editModule && this.parent.editModule['editedRecord'] && (!this.parent.editModule['editedRecord'][taskfields.startDate] && this.parent.editModule['editedRecord'][taskfields.endDate])) {
                cloneEndDate.setDate(cloneEndDate.getDate());
            }
            else {
                cloneEndDate.setDate(cloneEndDate.getDate() - 1);
            }
            dayEndTime = this.parent['getCurrentDayEndTime'](cloneEndDate);
            this.setTime(dayEndTime, cloneEndDate);
        }
        else if (hour > dayStartTime && hour < dayEndTime) {
            for (let index = 0; index < this.parent.workingTimeRanges.length; index++) {
                const value = this.parent.workingTimeRanges[index];
                if (hour > value.to && (this.parent.workingTimeRanges[index + 1] &&
                    hour <= this.parent.workingTimeRanges[index + 1].from)) {
                    this.setTime(this.parent.workingTimeRanges[index].to, cloneEndDate);
                    break;
                }
            }
        }
        let tempCheckDate;
        if (this.parent.autoCalculateDateScheduling && !ignoreAutoSchedule &&
            !(this.parent.isLoad && this.parent.treeGrid.loadChildOnDemand &&
                this.parent.taskFields.hasChildMapping)) {
            do {
                tempCheckDate = new Date(cloneEndDate.getTime());
                const holidayLength = calendarContext.defaultHolidays.length;
                if (this.isValidateNonWorkDays(ganttProp) && !isBaseline) {
                    if (!this.parent.includeWeekend) {
                        const tempDate = new Date(cloneEndDate.getTime());
                        cloneEndDate = this.getPreviousWorkingDay(cloneEndDate, calendarContext);
                        dayEndTime = this.parent['getCurrentDayEndTime'](cloneEndDate);
                        if (tempDate.getTime() !== cloneEndDate.getTime()) {
                            this.setTime(dayEndTime, cloneEndDate);
                        }
                    }
                    for (let count = 0; count < holidayLength; count++) {
                        const holidayFrom = this.getDateFromFormat(new Date(calendarContext.defaultHolidays[count]));
                        const holidayTo = new Date(holidayFrom.getTime());
                        const tempHoliday = new Date(cloneEndDate.getTime());
                        tempHoliday.setMinutes(cloneEndDate.getMilliseconds() - 2);
                        holidayFrom.setHours(0, 0, 0, 0);
                        holidayTo.setHours(23, 59, 59, 59);
                        if (cloneEndDate.getTime() >= holidayFrom.getTime() && cloneEndDate.getTime() < holidayTo.getTime() ||
                            tempHoliday.getTime() >= holidayFrom.getTime() && tempHoliday.getTime() < holidayTo.getTime()) {
                            cloneEndDate.setDate(cloneEndDate.getDate() - 1);
                            dayEndTime = this.parent['getCurrentDayEndTime'](cloneEndDate);
                            if (!(cloneEndDate.getTime() === holidayFrom.getTime() && dayEndTime === 86400 &&
                                this.getSecondsInDecimal(cloneEndDate) === 0)) {
                                this.setTime(dayEndTime, cloneEndDate);
                            }
                        }
                    }
                }
            } while (tempCheckDate.getTime() !== cloneEndDate.getTime());
            return new Date(cloneEndDate.getTime());
        }
        else {
            if (!isNullOrUndefined(cloneEndDate) && this.parent.defaultEndTime !== 86400) {
                dayEndTime = this.parent['getCurrentDayEndTime'](date);
                this.setTime(dayEndTime, cloneEndDate);
            }
            return new Date(cloneEndDate.getTime());
        }
    }
    /**
     * To validate the baseline start date
     *
     * @param {Date} date .
     * @param {ITaskData} ganttProp .
     * @returns {Date} .
     * @private
     */
    checkBaselineStartDate(date, ganttProp) {
        if (isNullOrUndefined(date)) {
            return null;
        }
        else {
            let dayStartTime = this.parent['getCurrentDayStartTime'](date);
            const dayEndTime = this.parent['getCurrentDayEndTime'](ganttProp ? ganttProp.endDate ? ganttProp.isAutoSchedule ? ganttProp.endDate : ganttProp.autoEndDate : date : date);
            const cloneDate = new Date(date.getTime());
            const hour = this.getSecondsInDecimal(cloneDate);
            if (hour < dayStartTime) {
                this.setTime(dayStartTime, cloneDate);
            }
            else if (hour > dayEndTime) {
                cloneDate.setDate(cloneDate.getDate() + 1);
                if (this.parent.weekWorkingTime.length > 0) {
                    dayStartTime = this.parent['getStartTime'](cloneDate);
                }
                else {
                    dayStartTime = this.parent.defaultStartTime;
                }
                this.setTime(dayStartTime, cloneDate);
            }
            else if (hour > dayStartTime && hour < dayEndTime) {
                for (let i = 0; i < this.parent.workingTimeRanges.length; i++) {
                    const value = this.parent.workingTimeRanges[i];
                    if (hour > value.to && (this.parent.workingTimeRanges[i + 1] &&
                        hour < this.parent.workingTimeRanges[i + 1].from)) {
                        this.setTime(this.parent.workingTimeRanges[i + 1].from, cloneDate);
                        break;
                    }
                }
            }
            return cloneDate;
        }
    }
    /**
     * To validate baseline end date
     *
     * @param {Date} date .
     * @param {ITaskData} ganttProp .
     * @returns {Date} .
     * @private
     */
    checkBaselineEndDate(date, ganttProp) {
        if (isNullOrUndefined(date)) {
            return null;
        }
        else {
            let dayEndTime = this.parent['getCurrentDayEndTime'](date);
            const dayStartTime = this.parent['getCurrentDayStartTime'](ganttProp ? ganttProp.startDate ? ganttProp.isAutoSchedule ? ganttProp.startDate : ganttProp.autoStartDate : date : date);
            const cloneDate = new Date(date.getTime());
            const hour = this.getSecondsInDecimal(cloneDate);
            if (hour > dayEndTime) {
                this.setTime(dayEndTime, cloneDate);
            }
            else if (hour < dayStartTime && !isNullOrUndefined(ganttProp) && !ganttProp.isMilestone) {
                cloneDate.setDate(cloneDate.getDate() - 1);
                dayEndTime = this.parent['getCurrentDayEndTime'](cloneDate);
                this.setTime(dayEndTime, cloneDate);
            }
            else if (hour > dayStartTime && hour < dayEndTime) {
                for (let i = 0; i < this.parent.workingTimeRanges.length; i++) {
                    const value = this.parent.workingTimeRanges[i];
                    if (hour > value.to && (this.parent.workingTimeRanges[i + 1] && hour <= this.parent.workingTimeRanges[i + 1].from)) {
                        this.setTime(this.parent.workingTimeRanges[i].to, cloneDate);
                        break;
                    }
                }
            }
            if (ganttProp && ganttProp.baselineStartDate && cloneDate &&
                ganttProp.baselineStartDate.getTime() > cloneDate.getTime()) {
                cloneDate.setDate(cloneDate.getDate() + 1);
            }
            return cloneDate;
        }
    }
    /**
     * To calculate start date value from duration and end date
     *
     * @param {IGanttData} ganttData - Defines the gantt data.
     * @param {boolean} isBaseline - Indicates whether the calculation is specific to baseline dates.
     * @returns {void} .
     * @private
     */
    calculateStartDate(ganttData, isBaseline) {
        const ganttProp = ganttData.ganttProperties;
        let tempStartDate = null;
        if (isBaseline) {
            if (!isNullOrUndefined(ganttProp.baselineEndDate) && !isNullOrUndefined(ganttProp.baselineDuration)) {
                tempStartDate = this.getStartDate(ganttProp.baselineEndDate, ganttProp.baselineDuration, ganttProp.durationUnit, ganttProp, undefined, true);
            }
            this.parent.setRecordValue('baselineStartDate', tempStartDate, ganttProp, true);
            if (this.parent.taskFields.baselineStartDate) {
                this.parent.dataOperation.updateMappingData(ganttData, 'baselineStartDate');
            }
        }
        else {
            if (!isNullOrUndefined(ganttProp.endDate) && !isNullOrUndefined(ganttProp.duration)) {
                tempStartDate = this.getStartDate(ganttProp.endDate, ganttProp.duration, ganttProp.durationUnit, ganttProp);
            }
            this.parent.setRecordValue('startDate', tempStartDate, ganttProp, true);
            if (this.parent.taskFields.startDate) {
                this.parent.dataOperation.updateMappingData(ganttData, 'startDate');
            }
        }
    }
    /**
     * Gets task field mappings based on baseline context.
     *
     * @param {boolean} isBaseline - Flag indicating if baseline fields should be used.
     * @returns {{ startdateField: string, enddateField: string, durationField: string }} - Object containing field names for start date, end date, and duration.
     */
    getFieldMappings(isBaseline) {
        return {
            startdateField: isBaseline ? 'baselineStartDate' : 'startDate',
            enddateField: isBaseline ? 'baselineEndDate' : 'endDate',
            durationField: isBaseline ? 'baselineDuration' : 'duration'
        };
    }
    /**
     *
     * @param {IGanttData} ganttData - Defines the gantt data.
     * @param {boolean} isBaseline - Indicates whether the calculation is specific to baseline dates.
     * @returns {void} .
     * @public
     */
    /* eslint-disable */
    calculateEndDate(ganttData, isBaseline) {
        const ganttProp = ganttData.ganttProperties;
        let tempEndDate = null;
        let dayStartTime;
        let dayEndTime;
        const { startdateField, enddateField, durationField } = this.getFieldMappings(isBaseline);
        if (!isNullOrUndefined(ganttProp[startdateField])) {
            if (!isNullOrUndefined(ganttProp[enddateField]) && isNullOrUndefined(ganttProp[durationField])) {
                if (this.compareDates(ganttProp[startdateField], ganttProp[enddateField]) === 1) {
                    this.parent.setRecordValue(startdateField, new Date(ganttProp[enddateField].getTime()), ganttProp, true);
                    dayStartTime = this.parent['getCurrentDayStartTime'](ganttProp.isAutoSchedule && !isBaseline ? ganttProp.autoStartDate : ganttProp[startdateField]);
                    dayEndTime = this.parent['getCurrentDayEndTime'](ganttProp.isAutoSchedule && !isBaseline ? ganttProp.autoEndDate : ganttProp[enddateField]);
                    this.setTime(dayStartTime, ganttProp[startdateField]);
                }
                this.calculateDuration(ganttData, isBaseline);
            }
            if (!isNullOrUndefined(ganttProp[durationField])) {
                const duration = !isBaseline && !isNullOrUndefined(ganttProp.segments) && ganttProp.segments.length > 1 ?
                    this.totalDuration(ganttProp.segments) : ganttProp[durationField];
                tempEndDate = this.getEndDate(ganttProp[startdateField], duration, ganttProp.durationUnit, ganttProp, false, isBaseline, ganttData);
            }
            this.parent.setRecordValue(enddateField, tempEndDate, ganttProp, true);
        }
        else {
            tempEndDate = ganttData[this.parent.taskFields[enddateField]];
            if (!isNullOrUndefined(tempEndDate)) {
                dayEndTime = this.parent['getCurrentDayEndTime'](tempEndDate);
                this.setTime(dayEndTime, tempEndDate);
            }
            this.parent.setRecordValue(enddateField, tempEndDate, ganttProp, true);
        }
        if (this.parent.taskFields[enddateField]) {
            this.parent.dataOperation.updateMappingData(ganttData, enddateField);
        }
    }
    /* eslint-enable */
    totalDuration(segments) {
        let duration = 0;
        let unit;
        if (!isNullOrUndefined(this.parent.timelineSettings.bottomTier)) {
            unit = this.parent.timelineSettings.bottomTier.unit;
        }
        for (let i = 0; i < segments.length; i++) {
            let segmentOffset = segments[i].offsetDuration;
            if (unit === 'Hour') {
                segmentOffset = segmentOffset / 24;
            }
            else if (unit === 'Minutes') {
                segmentOffset = segmentOffset / (24 * 60);
            }
            duration += segments[i].duration + segmentOffset;
        }
        duration = Math.ceil(duration);
        return duration;
    }
    /**
     * To calculate duration from start date and end date
     *
     * @param {IGanttData} ganttData - Defines the gantt data.
     * @param {boolean} isBaseline - Indicates whether the calculation is specific to baseline dates.
     * @returns {void} .
     */
    calculateDuration(ganttData, isBaseline) {
        const ganttProperties = ganttData.ganttProperties;
        const calendarContext = ganttProperties.calendarContext;
        let tDuration;
        if (!isBaseline && !isNullOrUndefined(ganttProperties.segments) && ganttProperties.segments.length > 0 &&
            !isNullOrUndefined(this.parent.editModule.taskbarEditModule)) {
            tDuration = this.parent.editModule.taskbarEditModule.sumOfDuration(ganttProperties.segments);
        }
        else {
            if (!isBaseline && (!isNullOrUndefined(this.parent.taskFields.milestone)) && (!isNullOrUndefined(ganttProperties.startDate))
                && !isNullOrUndefined(ganttProperties.endDate) &&
                (ganttProperties.startDate).getTime() === (ganttProperties.endDate).getTime()
                && (ganttData.taskData[this.parent.taskFields.milestone] === false)) {
                tDuration = 1;
            }
            else {
                const startDate = isBaseline ? ganttProperties.baselineStartDate : ganttProperties.startDate;
                const endDate = isBaseline ? ganttProperties.baselineEndDate : ganttProperties.endDate;
                const durationUnit = ganttProperties.durationUnit;
                const isAutoSchedule = isBaseline ? false : ganttProperties.isAutoSchedule;
                const isMilestone = isBaseline ? false : ganttProperties.isMilestone;
                tDuration = this.getDuration(startDate, endDate, durationUnit, isAutoSchedule, isMilestone, undefined, calendarContext);
            }
        }
        const duration = isBaseline ? 'baselineDuration' : 'duration';
        this.parent.setRecordValue(duration, tDuration, ganttProperties, true);
        const col = isBaseline ? this.parent.columnByField[this.parent.columnMapping.baselineDuration] :
            this.parent.columnByField[this.parent.columnMapping.duration];
        if (!isNullOrUndefined(this.parent.editModule) && !isNullOrUndefined(this.parent.editModule.cellEditModule) &&
            !this.parent.editModule.cellEditModule.isCellEdit && !isNullOrUndefined(col)) {
            if (!isNullOrUndefined(col.edit) && !isNullOrUndefined(col.edit.read)) {
                const dialog = this.parent.editModule.dialogModule.dialog;
                if (!isNullOrUndefined(dialog)) {
                    const textBoxElement = isBaseline ? dialog.querySelector('#' + this.parent.element.id + 'BaselineDuration') :
                        dialog.querySelector('#' + this.parent.element.id + 'Duration');
                    if (!isNullOrUndefined(textBoxElement)) {
                        const textBox = textBoxElement.ej2_instances[0];
                        if (!isNullOrUndefined(textBox) && textBox.value !== tDuration.toString()) {
                            textBox.value = tDuration.toString();
                            textBox.dataBind();
                        }
                    }
                }
            }
            if (this.parent.taskFields.duration || this.parent.taskFields.baselineDuration) {
                this.parent.dataOperation.updateMappingData(ganttData, duration);
                if (this.parent.taskFields.durationUnit) {
                    this.parent.dataOperation.updateMappingData(ganttData, 'durationUnit');
                }
            }
        }
    }
    /**
     *
     * @param {Date} sDate Method to get total nonworking time between two date values
     * @param {Date} eDate .
     * @param {boolean} isAutoSchedule .
     * @param {boolean} isCheckTimeZone .
     * @param {CalendarContext} calendarContext .
     * @param {boolean} isBaseline - Indicates whether the calculation is specific to baseline dates.
     * @param {boolean} ignoreAutoSchedule - Indicates whether to consider the autoschedule or not.
     * @returns {number} .
     */
    getNonworkingTime(sDate, eDate, isAutoSchedule, isCheckTimeZone, calendarContext, isBaseline, ignoreAutoSchedule) {
        const parent = this.parent;
        isCheckTimeZone = isNullOrUndefined(isCheckTimeZone) ? true : isCheckTimeZone;
        const shouldCheckWeekend = !isBaseline &&
            !parent.includeWeekend &&
            parent.autoCalculateDateScheduling &&
            !(parent.isLoad && parent.treeGrid.loadChildOnDemand && parent.taskFields.hasChildMapping) &&
            isAutoSchedule;
        const weekendCount = shouldCheckWeekend && !ignoreAutoSchedule ? this.getWeekendCount(sDate, eDate, calendarContext) : 0;
        const totalSeconds = this.getNumberOfSeconds(sDate, eDate, isCheckTimeZone);
        const shouldCheckHolidays = (isAutoSchedule && parent.autoCalculateDateScheduling &&
            !(parent.isLoad && parent.treeGrid.loadChildOnDemand && parent.taskFields.hasChildMapping)) && !isBaseline;
        const holidaysCount = shouldCheckHolidays && !ignoreAutoSchedule ? this.getHolidaysCount(sDate, eDate, calendarContext) : 0;
        const totalWorkDays = (totalSeconds - (weekendCount * 86400) - (holidaysCount * 86400)) / 86400;
        const nonWorkingSecondsOnDate = this.getNonWorkingSecondsOnDate(sDate, eDate, isAutoSchedule, isBaseline, calendarContext, ignoreAutoSchedule);
        const nonWorkingTime = parent.weekWorkingTime.length > 0 ?
            this.nonWorkingSeconds(sDate, eDate, isAutoSchedule, totalWorkDays, calendarContext, false, ignoreAutoSchedule) :
            (totalWorkDays * (86400 - parent.secondsPerDay));
        return nonWorkingTime + (weekendCount * 86400) + (holidaysCount * 86400) + nonWorkingSecondsOnDate;
    }
    nonWorkingSeconds(sDate, eDate, isAutoSchedule, workDays, calendarContext, fromDuration, ignoreAutoSchedule) {
        const newStartDate = sDate.getTime() > eDate.getTime() ? new Date(eDate.getTime()) : new Date(sDate.getTime());
        const newEndDate = sDate.getTime() > eDate.getTime() ? new Date(sDate.getTime()) : new Date(eDate.getTime());
        let timeDiff = 0;
        let count = 0;
        if (fromDuration) {
            const dayStartTime = this.parent['getCurrentDayStartTime'](newStartDate);
            const dayEndTime = this.parent['getCurrentDayEndTime'](newStartDate);
            if (!(newStartDate.getHours() < dayEndTime / 3600 && newStartDate.getHours() >= dayStartTime / 3600)) {
                newStartDate.setDate(newStartDate.getDate() + 1);
            }
        }
        else {
            newStartDate.setDate(newStartDate.getDate() + 1);
            newStartDate.setHours(0, 0, 0, 0);
            newEndDate.setHours(0, 0, 0, 0);
        }
        if (workDays > 0 || isNullOrUndefined(workDays)) {
            while ((fromDuration && newStartDate.getTime() <= newEndDate.getTime())
                || (!fromDuration && newStartDate.getTime() < newEndDate.getTime())) {
                if (isAutoSchedule && !ignoreAutoSchedule) {
                    if (this.isOnHolidayOrWeekEnd(newStartDate, true, calendarContext)) {
                        do {
                            newStartDate.setDate(newStartDate.getDate() + 1);
                        } while (this.isOnHolidayOrWeekEnd(newStartDate, true, calendarContext));
                    }
                    if ((!this.parent.includeWeekend)) {
                        this.getNextWorkingDay(newStartDate, calendarContext);
                    }
                }
                if (newStartDate.getTime() <= newEndDate.getTime()) {
                    count++;
                    const currentDaySeconds = this.parent['getSecondsPerDay'](newStartDate);
                    if (fromDuration) {
                        timeDiff += currentDaySeconds;
                    }
                    else {
                        timeDiff += 86400 - currentDaySeconds;
                    }
                    newStartDate.setDate(newStartDate.getDate() + 1);
                    if (isAutoSchedule && !ignoreAutoSchedule) {
                        if (this.isOnHolidayOrWeekEnd(newStartDate, true, calendarContext)) {
                            do {
                                newStartDate.setDate(newStartDate.getDate() + 1);
                            } while (this.isOnHolidayOrWeekEnd(newStartDate, true, calendarContext));
                        }
                        if ((!this.parent.includeWeekend)) {
                            this.getNextWorkingDay(newStartDate, calendarContext);
                        }
                    }
                }
            }
        }
        else {
            return 0;
        }
        if (fromDuration) {
            if (timeDiff > 0) {
                timeDiff = timeDiff / count;
            }
            else {
                timeDiff = this.parent.secondsPerDay;
            }
        }
        return timeDiff;
    }
    /**
     *
     * @param {Date} startDate .
     * @param {Date} endDate .
     * @param {string} durationUnit .
     * @param {boolean} isAutoSchedule .
     * @param {boolean} isMilestone .
     * @param {boolean} isCheckTimeZone .
     * @param {CalendarContext} calendarContext .
     * @returns {number} .
     * @private
     */
    getDuration(startDate, endDate, durationUnit, isAutoSchedule, isMilestone, isCheckTimeZone = true, calendarContext) {
        if (!startDate || !endDate) {
            return null;
        }
        let durationValue = 0;
        const isSameDay = this.parent.getFormatedDate(startDate) === this.parent.getFormatedDate(endDate);
        let totSeconds;
        if (this.parent.weekWorkingTime.length > 0) {
            const tempStartDate = new Date(startDate);
            totSeconds = this.nonWorkingSeconds(startDate, endDate, isAutoSchedule, undefined, calendarContext, true);
            let totalDurationInDays = 0;
            while (tempStartDate <= endDate) {
                const currentDateString = this.parent.getFormatedDate(tempStartDate);
                const dayWorkingSeconds = this.parent['getSecondsPerDay'](tempStartDate);
                const currentStartDate = new Date(tempStartDate);
                const currentEndDate = new Date(tempStartDate);
                currentStartDate.setHours(0, 0, 0, 0);
                currentEndDate.setHours(0, 0, 0, 0);
                currentStartDate.setSeconds(this.parent['getCurrentDayStartTime'](tempStartDate));
                currentEndDate.setSeconds(this.parent['getCurrentDayEndTime'](tempStartDate));
                if (currentDateString === this.parent.getFormatedDate(startDate)) {
                    currentStartDate.setTime(startDate.getTime());
                }
                if (currentDateString === this.parent.getFormatedDate(endDate)) {
                    currentEndDate.setTime(endDate.getTime());
                }
                const timeDiffSeconds = this.getTimeDifference(currentStartDate, currentEndDate, isCheckTimeZone) / 1000;
                const nonWorkSeconds = this.getNonworkingTime(currentStartDate, currentEndDate, isAutoSchedule, isCheckTimeZone, calendarContext);
                totalDurationInDays += Math.max(0, timeDiffSeconds - nonWorkSeconds) / dayWorkingSeconds;
                tempStartDate.setDate(tempStartDate.getDate() + 1);
            }
            if (!(isMilestone && isSameDay)) {
                durationValue = this.calculateDurationValue(durationUnit, totalDurationInDays, totSeconds);
            }
        }
        else {
            totSeconds = this.parent.secondsPerDay;
            const timeDiff = this.getTimeDifference(startDate, endDate, isCheckTimeZone) / 1000;
            const nonWorkHours = this.getNonworkingTime(startDate, endDate, isAutoSchedule, isCheckTimeZone, calendarContext);
            const durationHours = timeDiff - nonWorkHours;
            if (!(isMilestone && isSameDay)) {
                durationValue = this.calculateDurationValue(durationUnit, durationHours / totSeconds, totSeconds);
            }
        }
        return parseFloat(durationValue.toString());
    }
    calculateDurationValue(durationUnit, duration, totSeconds) {
        if (!durationUnit || durationUnit === 'day') {
            return duration;
        }
        else if (durationUnit === 'minute') {
            return duration * (totSeconds / 60);
        }
        else {
            return duration * (totSeconds / 3600);
        }
    }
    /**
     *
     * @param {number} duration .
     * @param {string} durationUnit .
     * @param {Date} date .
     *  @returns {number} .
     */
    getDurationAsSeconds(duration, durationUnit, date) {
        let value = 0;
        let totSeconds;
        if (this.parent.weekWorkingTime.length > 0) {
            totSeconds = this.parent['getSecondsPerDay'](date);
        }
        else {
            totSeconds = this.parent.secondsPerDay;
        }
        if (!durationUnit || durationUnit.toLocaleLowerCase() === 'day') {
            value = totSeconds * duration;
        }
        else if (durationUnit.toLocaleLowerCase() === 'hour') {
            value = duration * 3600;
        }
        else {
            value = duration * 60;
        }
        return value;
    }
    /**
     * To get date from start date and duration
     *
     * @param {Date} startDate .
     * @param {number} duration .
     * @param {string} durationUnit .
     * @param {ITaskData} ganttProp .
     * @param {boolean} validateAsMilestone .
     * @param {boolean} isBaseline - Indicates whether the calculation is specific to baseline dates.
     * @param {IGanttData} ganttData .
     * @returns {Date} .
     * @private
     */
    getEndDate(startDate, duration, durationUnit, ganttProp, validateAsMilestone, isBaseline, ganttData) {
        const sDate = new Date(startDate.getTime());
        let secondDuration;
        if (!ganttProp.calendarContext) {
            ganttProp.calendarContext = this.parent.defaultCalendarContext;
        }
        if (this.parent.weekWorkingTime.length > 0 &&
            (!durationUnit || durationUnit.toLocaleLowerCase() === 'day')) {
            secondDuration = this.calculateSecondDuration(duration, sDate, secondDuration, startDate, true, ganttProp.calendarContext);
        }
        else {
            secondDuration = this.getDurationAsSeconds(duration, durationUnit, startDate);
        }
        // Calculate actual end date considering working time
        const endDate = this.calculateEndDateFromDuration(startDate, secondDuration, ganttProp, validateAsMilestone, ganttProp.calendarContext, isBaseline, false);
        // Handle auto-validated tasks for dataSource have startdate and duration, no enddate value mapping case:
        if (this.parent.autoCalculateDateScheduling && this.parent.isLoad && !isNullOrUndefined(ganttData) &&
            isNullOrUndefined(ganttData[this.parent.taskFields.endDate])
            && !isNullOrUndefined(ganttData[this.parent.taskFields.startDate])
            && !ganttData.hasChildRecords) {
            const userStartDate = this.getDateFromFormat(ganttData[this.parent.taskFields.startDate]);
            this.parent.dataOperation['resolveAndApplyWorkingTimes'](userStartDate);
            const formattedStartDate = this.checkStartDate(userStartDate, ganttProp, validateAsMilestone, this.parent.isLoad, false, true);
            const naiveSeconds = this.getDurationAsSeconds(duration, durationUnit, userStartDate);
            const naiveEndDate = this.calculateEndDateFromDuration(formattedStartDate, naiveSeconds, ganttProp, validateAsMilestone, ganttProp.calendarContext, false, ganttProp.isAutoSchedule);
            // Compare the dates: if calculated end date is greater than naive end date, push to collection
            if (endDate.getTime() !== naiveEndDate.getTime() || userStartDate.getTime() !== formattedStartDate.getTime()) {
                this.parent.dataOperation['validatedGanttData'].set(ganttData.ganttProperties.taskId, ganttData);
            }
        }
        return endDate;
    }
    calculateEndDateFromDuration(startDate, totalSeconds, ganttProp, validateAsMilestone, calendarContext, isBaseline, ignoreAutoSchedule) {
        let current = new Date(startDate.getTime());
        let endDate = new Date(startDate.getTime());
        let remainingSeconds = totalSeconds;
        while (remainingSeconds > 0) {
            endDate.setSeconds(endDate.getSeconds() + remainingSeconds);
            const nonWork = this.getNonworkingTime(current, endDate, ganttProp.isAutoSchedule, true, calendarContext, isBaseline, ignoreAutoSchedule);
            const workHours = remainingSeconds - nonWork;
            remainingSeconds -= workHours;
            if (remainingSeconds > 0) {
                endDate = this.checkStartDate(endDate, ganttProp, validateAsMilestone, undefined, isBaseline, ignoreAutoSchedule);
            }
            current = new Date(endDate.getTime());
        }
        return endDate;
    }
    /**
     * Calculate start date based on end date and duration.
     *
     * @param {Date} endDate - To calculate start date value from end date and duration.
     * @param {number} duration - The duration value.
     * @param {string} durationUnit - The unit of duration.
     * @param {ITaskData} ganttProp - The Gantt task properties.
     * @param {boolean} fromValidation - A flag indicating if the calculation is from validation.
     * @param {boolean} isBaseline - Indicates whether the calculation is specific to baseline dates.
     * @returns {Date} The calculated start date.
     * @private
     */
    getStartDate(endDate, duration, durationUnit, ganttProp, fromValidation, isBaseline) {
        let tempEnd = new Date(endDate.getTime());
        let startDate = new Date(endDate.getTime());
        let secondDuration;
        const eDate = new Date(tempEnd.getTime());
        if (!ganttProp.calendarContext) {
            ganttProp.calendarContext = this.parent.defaultCalendarContext;
        }
        if (this.parent.weekWorkingTime.length > 0) {
            secondDuration = this.calculateSecondDuration(duration, eDate, secondDuration, tempEnd, false, ganttProp.calendarContext);
        }
        else {
            secondDuration = this.getDurationAsSeconds(duration, durationUnit, tempEnd);
        }
        let nonWork = 0;
        let workHours = 0;
        while (secondDuration > 0) {
            startDate.setSeconds(startDate.getSeconds() - secondDuration);
            nonWork = this.getNonworkingTime(startDate, tempEnd, ganttProp.isAutoSchedule, true, ganttProp.calendarContext, isBaseline);
            workHours = secondDuration - nonWork;
            secondDuration = secondDuration - workHours;
            if (secondDuration > 0) {
                tempEnd = this.checkEndDate(startDate, ganttProp, isBaseline);
            }
            tempEnd = new Date(startDate.getTime());
        }
        /* To render the milestone in proper date while loading */
        if (fromValidation && ganttProp.isMilestone) {
            startDate.setDate(startDate.getDate() - 1);
            const dayEndTime = this.parent['getCurrentDayEndTime'](ganttProp.endDate ? ganttProp.isAutoSchedule ? ganttProp.endDate : ganttProp.autoEndDate : startDate);
            this.parent.dateValidationModule.setTime(dayEndTime, startDate);
            startDate = this.parent.dateValidationModule.checkStartDate(startDate, ganttProp, true);
        }
        return startDate;
    }
    calculateSecondDuration(duration, sDate, secondDuration, startDate, fromEndDate, calendarContext) {
        if (duration < 1) {
            secondDuration = this.parent['getSecondsPerDay'](sDate) * duration;
        }
        else {
            secondDuration = 0;
            let durationValue = duration;
            const dayStartTime = this.parent['getCurrentDayStartTime'](sDate);
            const dayEndTime = this.parent['getCurrentDayEndTime'](sDate);
            if (!(sDate.getHours() < dayEndTime / 3600 && sDate.getHours() > dayStartTime / 3600) && this.fromSegments) {
                if (fromEndDate) {
                    sDate.setDate(sDate.getDate() + 1);
                }
                else {
                    sDate.setDate(sDate.getDate() - 1);
                }
            }
            while (durationValue > 0) {
                if (this.isOnHolidayOrWeekEnd(sDate, true, calendarContext)) {
                    do {
                        if (fromEndDate) {
                            sDate.setDate(sDate.getDate() + 1);
                        }
                        else {
                            sDate.setDate(sDate.getDate() - 1);
                        }
                    } while (this.isOnHolidayOrWeekEnd(sDate, true, calendarContext));
                }
                if ((!this.parent.includeWeekend)) {
                    sDate = fromEndDate ?
                        this.getNextWorkingDay(sDate, calendarContext) :
                        this.getPreviousWorkingDay(sDate, calendarContext);
                }
                let totSeconds = this.parent['getSecondsPerDay'](sDate);
                let num = 0;
                if (this.getSecondsInDecimal(startDate) !== this.parent['getStartTime'](startDate) && !Number.isInteger(durationValue)) {
                    const deciNumber = duration.toString().split('.');
                    num = parseFloat('.' + deciNumber[1]);
                    totSeconds = totSeconds * num;
                    durationValue = durationValue - num;
                }
                if (durationValue < 1) {
                    totSeconds = totSeconds * durationValue;
                }
                secondDuration = secondDuration + totSeconds;
                if (fromEndDate) {
                    sDate.setDate(sDate.getDate() + 1);
                }
                else {
                    sDate.setDate(sDate.getDate() - 1);
                }
                if (!num) {
                    durationValue--;
                }
            }
        }
        return secondDuration;
    }
    /**
     * @param {ITaskData} ganttProp .
     * @param {boolean} isLoad .
     * @returns {Date} .
     * @private
     */
    getProjectStartDate(ganttProp, isLoad) {
        if (!isNullOrUndefined(this.parent.cloneProjectStartDate)) {
            if (typeof this.parent.cloneProjectStartDate === 'string') {
                this.parent.cloneProjectStartDate = this.getDateFromFormat(this.parent.cloneProjectStartDate);
            }
            const cloneStartDate = this.checkStartDate(this.parent.cloneProjectStartDate);
            this.parent.cloneProjectStartDate = cloneStartDate;
            return new Date(cloneStartDate.getTime());
        }
        else if (!isNullOrUndefined(this.parent.projectStartDate)) {
            const cloneStartDate = this.getDateFromFormat(this.parent.projectStartDate);
            this.parent.cloneProjectStartDate = this.checkStartDate(cloneStartDate);
        }
        else if (!isNullOrUndefined(isLoad)) {
            const flatData = this.parent.flatData;
            let minStartDate;
            if (flatData.length > 0) {
                minStartDate = flatData[0].ganttProperties.startDate;
            }
            else {
                minStartDate = new Date();
                minStartDate.setHours(0, 0, 0, 0);
            }
            for (let index = 1; index < flatData.length; index++) {
                const startDate = flatData[index].ganttProperties.startDate;
                if (!isNullOrUndefined(startDate) && this.compareDates(startDate, minStartDate) === -1) {
                    minStartDate = startDate;
                }
            }
            this.parent.cloneProjectStartDate = this.checkStartDate(minStartDate, ganttProp);
        }
        else {
            return null;
        }
        return new Date(this.parent.cloneProjectStartDate.getTime());
    }
    /**
     * @param {ITaskData} ganttProp .
     * @param {boolean} isAuto .
     * @param {boolean} isBaseline .
     * @returns {Date} .
     * @private
     */
    getValidStartDate(ganttProp, isAuto, isBaseline) {
        let sDate = null;
        const startDate = isBaseline ? ganttProp.baselineStartDate : (isAuto ? ganttProp.autoStartDate : ganttProp.startDate);
        const endDate = isBaseline ? ganttProp.baselineEndDate : (isAuto ? ganttProp.autoEndDate : ganttProp.endDate);
        const duration = !ganttProp.isAutoSchedule && ganttProp.autoDuration ? ganttProp.autoDuration : ganttProp.duration;
        if (isNullOrUndefined(startDate)) {
            if (!isNullOrUndefined(endDate)) {
                sDate = new Date(endDate.getTime());
                const dayStartTime = this.parent['getCurrentDayStartTime'](sDate);
                this.setTime(dayStartTime, sDate);
            }
            else if (!isNullOrUndefined(duration)) {
                const ganttTask = this.parent.getTaskByUniqueID(ganttProp.uniqueID);
                if (this.parent.allowUnscheduledTasks && ganttTask &&
                    ganttTask.parentItem && isNullOrUndefined(startDate) && isNullOrUndefined(endDate)) {
                    let parentTask = this.parent.getParentTask(ganttTask.parentItem);
                    while (parentTask && !parentTask.ganttProperties.startDate) {
                        parentTask = this.parent.getParentTask(parentTask.parentItem);
                    }
                    sDate = (!parentTask || !parentTask.ganttProperties.startDate) ? this.parent.cloneProjectStartDate
                        : parentTask.ganttProperties.startDate;
                }
                else {
                    sDate = this.getProjectStartDate(ganttProp);
                }
            }
        }
        else {
            sDate = new Date(startDate.getTime());
        }
        return sDate;
    }
    /**
     *
     * @param {ITaskData} ganttProp .
     * @param {boolean} isAuto .
     * @param {boolean} isBaseline .
     * @returns {Date} .
     * @private
     */
    getValidEndDate(ganttProp, isAuto, isBaseline) {
        let eDate = null;
        const startDate = isBaseline ? ganttProp.baselineStartDate : (isAuto ? ganttProp.autoStartDate : ganttProp.startDate);
        const endDate = isBaseline ? ganttProp.baselineEndDate : (isAuto ? ganttProp.autoEndDate : ganttProp.endDate);
        const duration = isAuto ? ganttProp.autoDuration : ganttProp.duration;
        if (isNullOrUndefined(endDate)) {
            if (!isNullOrUndefined(startDate)) {
                if (ganttProp.isMilestone) {
                    eDate = this.checkStartDate(startDate);
                }
                else {
                    eDate = new Date(startDate.getTime());
                    const dayEndTime = this.parent['getCurrentDayEndTime'](endDate ? endDate : eDate);
                    this.setTime(dayEndTime, eDate);
                }
            }
            else if (!isNullOrUndefined(duration)) {
                const sDate = this.getValidStartDate(ganttProp);
                if (sDate) {
                    eDate = this.getEndDate(sDate, duration, ganttProp.durationUnit, ganttProp, false);
                }
            }
        }
        else {
            eDate = new Date(endDate.getTime());
        }
        return eDate;
    }
    getWorkingTime(day, currentRange, startDate, totalSeconds, count, nonWorkingHours, workingTimeRanges, nonWorkingTimeRanges) {
        if (!isNullOrUndefined(currentRange.from) && !isNullOrUndefined(currentRange.to)) {
            startDate.setHours(0, 0, 0, 0);
            const tempDate = new Date(startDate.getTime());
            startDate.setTime(startDate.getTime() + (currentRange.from * 3600000));
            const startHour = new Date(startDate.getTime());
            if (currentRange.to === 24) {
                const currentRangeTo = 24 * 60 * 60 * 1000;
                tempDate.setTime(tempDate.getTime() + (currentRangeTo));
            }
            else {
                tempDate.setTime(tempDate.getTime() + (currentRange.to * 3600000));
            }
            const endHour = new Date(tempDate.getTime());
            const timeDiff = endHour.getTime() - startHour.getTime();
            const sdSeconds = this.getSecondsInDecimal(startHour);
            let edSeconds = this.getSecondsInDecimal(endHour);
            if (edSeconds === 0) {
                edSeconds = 86400;
            }
            totalSeconds += timeDiff / 1000;
            if (count === 0) {
                this.parent.defaultStartTime = sdSeconds;
                if (this.parent.weekWorkingTime.length > 0) {
                    this.assignStartTime(day, sdSeconds);
                }
            }
            if (count === this[day.toLowerCase() + 'TimeRangeLength'] - 1 || day === '') {
                this.parent.defaultEndTime = edSeconds;
                if (this.parent.weekWorkingTime.length > 0) {
                    this.assignEndTime(day, edSeconds);
                }
            }
            if (count > 0) {
                if (day === '') {
                    nonWorkingHours.push(nonWorkingHours[nonWorkingHours.length - 1] +
                        sdSeconds - workingTimeRanges[count - 1].to);
                    if (workingTimeRanges[count - 1].to < sdSeconds) {
                        nonWorkingTimeRanges.push({
                            from: workingTimeRanges[count - 1].to, to: sdSeconds, isWorking: false,
                            interval: (sdSeconds - workingTimeRanges[count - 1].to)
                        });
                    }
                }
                else {
                    this.parent[day.toLowerCase() + 'NonWorkingHours'].push(this.parent[day.toLowerCase() + 'NonWorkingHours'][this.parent[day.toLowerCase() + 'NonWorkingHours'].length - 1] +
                        sdSeconds - this.parent[day.toLowerCase() + 'NonWorkingTimeRanges'][count - 1].to);
                    if (this.parent[day.toLowerCase() + 'WorkingTimeRanges'][count - 1].to < sdSeconds) {
                        this.parent[day.toLowerCase() + 'NonWorkingTimeRanges'].push({
                            from: this.parent[day.toLowerCase() + 'WorkingTimeRanges'][count - 1].to, to: sdSeconds, isWorking: false,
                            interval: (sdSeconds - this.parent[day.toLowerCase() + 'WorkingTimeRanges'][count - 1].to)
                        });
                    }
                }
            }
            else {
                if (day === '') {
                    nonWorkingHours.push(0);
                    nonWorkingTimeRanges.push({ from: 0, to: sdSeconds, isWorking: false, interval: sdSeconds });
                }
                else {
                    this.parent[day.toLowerCase() + 'NonWorkingHours'].push(0);
                    this.parent[day.toLowerCase() + 'NonWorkingTimeRanges'].push({ from: 0, to: sdSeconds, isWorking: false, interval: sdSeconds });
                }
            }
            if (day === '') {
                workingTimeRanges.push({ from: sdSeconds, to: edSeconds });
                nonWorkingTimeRanges.push({
                    from: sdSeconds, to: edSeconds, isWorking: true, interval: (edSeconds - sdSeconds)
                });
            }
            else {
                this.parent[day.toLowerCase() + 'WorkingTimeRanges'].push({ from: sdSeconds, to: edSeconds });
                this.parent[day.toLowerCase() + 'NonWorkingTimeRanges'].push({
                    from: sdSeconds, to: edSeconds, isWorking: true, interval: (edSeconds - sdSeconds)
                });
            }
        }
        return totalSeconds;
    }
    assignStartTime(day, sdSeconds) {
        switch (day) {
            case 'Monday':
                this.parent.mondayDefaultStartTime = sdSeconds;
                break;
            case 'Tuesday':
                this.parent.tuesdayDefaultStartTime = sdSeconds;
                break;
            case 'Wednesday':
                this.parent.wednesdayDefaultStartTime = sdSeconds;
                break;
            case 'Thursday':
                this.parent.thursdayDefaultStartTime = sdSeconds;
                break;
            case 'Friday':
                this.parent.fridayDefaultStartTime = sdSeconds;
                break;
            case 'Saturday':
                this.parent.saturdayDefaultStartTime = sdSeconds;
                break;
            case 'Sunday':
                this.parent.sundayDefaultStartTime = sdSeconds;
                break;
            default:
                break;
        }
    }
    assignEndTime(day, edSeconds) {
        switch (day) {
            case 'Monday':
                this.parent.mondayDefaultEndTime = edSeconds;
                break;
            case 'Tuesday':
                this.parent.tuesdayDefaultEndTime = edSeconds;
                break;
            case 'Wednesday':
                this.parent.wednesdayDefaultEndTime = edSeconds;
                break;
            case 'Thursday':
                this.parent.thursdayDefaultEndTime = edSeconds;
                break;
            case 'Friday':
                this.parent.fridayDefaultEndTime = edSeconds;
                break;
            case 'Saturday':
                this.parent.saturdayDefaultEndTime = edSeconds;
                break;
            case 'Sunday':
                this.parent.sundayDefaultEndTime = edSeconds;
                break;
            default:
                break;
        }
    }
    calculateWeekWorkTime(day, currentDay, startDate, totalSeconds, nonWorkingHours, workingTimeRanges, nonWorkingTimeRanges) {
        let seconds = 0;
        for (let j = 0; j < currentDay.length; j++) {
            const currentRange = currentDay[j];
            seconds = seconds + this.getWorkingTime(day, currentRange, startDate, totalSeconds, j, nonWorkingHours, workingTimeRanges, nonWorkingTimeRanges);
        }
        if (this.parent[day.toLowerCase() + 'DefaultStartTime'] / 3600 !== 24) {
            this.parent[day.toLowerCase() + 'NonWorkingTimeRanges'].push({
                from: this.parent[day.toLowerCase() + 'DefaultEndTime'], to: 86400,
                isWorking: false, interval: 86400 - this.parent[day.toLowerCase() + 'DefaultEndTime']
            });
        }
        return seconds;
    }
    /**
     * @returns {number} .
     * @private
     */
    getSecondsPerDay() {
        let totalSeconds = 0;
        const startDate = new Date('10/11/2018');
        this.parent.nonWorkingHours = [];
        const nonWorkingHours = this.parent.nonWorkingHours;
        this.parent.workingTimeRanges = [];
        const workingTimeRanges = this.parent.workingTimeRanges;
        this.parent.nonWorkingTimeRanges = [];
        const nonWorkingTimeRanges = this.parent.nonWorkingTimeRanges;
        const dayWorkingTime = this.parent.calendarModule.workingTime;
        for (const day of ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']) {
            this[`${day.toLowerCase()}TimeRangeLength`] = dayWorkingTime.length;
        }
        if (this.parent.weekWorkingTime.length > 0) {
            for (let i = 0; i < this.parent.weekWorkingTime.length; i++) {
                this[this.parent.weekWorkingTime[i].dayOfWeek.toLowerCase() + 'TimeRangeLength']
                    = this.parent.weekWorkingTime[i].timeRange.length;
            }
            const weekWorkingTime = this.parent.weekWorkingTime;
            for (const weekDay of weekWorkingTime) {
                if (weekDay.timeRange && weekDay.timeRange.length > 0) {
                    this.parent[weekDay.dayOfWeek.toLowerCase() + 'NonWorkingHours'] = [];
                    this.parent[weekDay.dayOfWeek.toLowerCase() + 'NonWorkingTimeRanges'] = [];
                    this.parent[weekDay.dayOfWeek.toLowerCase() + 'WorkingTimeRanges'] = [];
                    const seconds = this.calculateWeekWorkTime(weekDay.dayOfWeek, weekDay.timeRange, startDate, totalSeconds, nonWorkingHours, workingTimeRanges, nonWorkingTimeRanges);
                    this.parent[`${weekDay.dayOfWeek.toLowerCase()}Seconds`] = seconds;
                }
            }
            for (const day of ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']) {
                const dayDefaultStartTime = this.parent[`${day.toLowerCase()}DefaultStartTime`];
                if (isNullOrUndefined(dayDefaultStartTime)) {
                    const seconds = this.calculateWeekWorkTime(day, dayWorkingTime, startDate, totalSeconds, nonWorkingHours, workingTimeRanges, nonWorkingTimeRanges);
                    this.parent[`${day.toLowerCase()}Seconds`] = seconds;
                }
            }
        }
        const length = dayWorkingTime.length;
        let seconds = 0;
        for (let count = 0; count < length; count++) {
            const currentRange = dayWorkingTime[count];
            seconds = seconds + this.getWorkingTime('', currentRange, startDate, totalSeconds, count, nonWorkingHours, workingTimeRanges, nonWorkingTimeRanges);
        }
        if (this.parent.defaultEndTime / 3600 !== 24) {
            nonWorkingTimeRanges.push({
                from: this.parent.defaultEndTime, to: 86400,
                isWorking: false, interval: 86400 - this.parent.defaultEndTime
            });
        }
        totalSeconds = seconds;
        return totalSeconds;
    }
    /**
     *
     * @param {string} value .
     * @returns {object} .
     * @private
     */
    // eslint-disable-next-line
    getDurationValue(value) {
        let durationUnit = null;
        let duration = null;
        if (typeof value === 'string') {
            const values = value.match(/(\d*\.*\d+|.+$)/g);
            if (values && values.length <= 2) {
                duration = parseFloat(values[0].toString().trim());
                const unit = values[1] ? values[1].toString().trim().toLowerCase() : null;
                if (getValue('minute', this.parent.durationUnitEditText).indexOf(unit) !== -1) {
                    durationUnit = 'minute';
                }
                else if (getValue('hour', this.parent.durationUnitEditText).indexOf(unit) !== -1) {
                    durationUnit = 'hour';
                }
                else if (getValue('day', this.parent.durationUnitEditText).indexOf(unit) !== -1) {
                    durationUnit = 'day';
                }
            }
        }
        else {
            duration = value;
            durationUnit = null;
        }
        const output = {
            duration: duration,
            durationUnit: durationUnit
        };
        return output;
    }
    /**
     *
     * @param {Date} date .
     * @param {CalendarContext} calendarContext .
     * @returns {Date} .
     */
    getNextWorkingDay(date, calendarContext) {
        if (calendarContext) {
            const override = calendarContext.getExceptionForDate(date);
            if (override) {
                return date;
            }
        }
        const dayIndex = date.getDay();
        if (this.parent.nonWorkingDayIndex.indexOf(dayIndex) !== -1) {
            date.setDate(date.getDate() + 1);
            date = this.getNextWorkingDay(date, calendarContext);
            return date;
        }
        else {
            return date;
        }
    }
    /**
     * get weekend days between two dates without including args dates
     *
     * @param {Date} startDate .
     * @param {Date} endDate .
     * @param {CalendarContext} calendarContext .
     * @returns {number} .
     */
    getWeekendCount(startDate, endDate, calendarContext) {
        let weekendCount = 0;
        const sDate = new Date(startDate.getTime());
        const eDate = new Date(endDate.getTime());
        sDate.setHours(0, 0, 0, 0);
        sDate.setDate(sDate.getDate() + 1);
        eDate.setHours(0, 0, 0, 0);
        while (sDate.getTime() < eDate.getTime()) {
            if (this.parent.nonWorkingDayIndex.indexOf(sDate.getDay()) !== -1) {
                if (!calendarContext.getExceptionForDate(sDate)) {
                    weekendCount += 1;
                }
            }
            sDate.setDate(sDate.getDate() + 1);
        }
        return weekendCount;
    }
    /**
     *
     * @param {Date} startDate .
     * @param {Date} endDate .
     * @param {boolean} isCheckTimeZone .
     * @returns {number} .
     */
    getNumberOfSeconds(startDate, endDate, isCheckTimeZone) {
        const sDate = new Date(startDate.getTime());
        const eDate = new Date(endDate.getTime());
        let timeDiff = 0;
        sDate.setDate(sDate.getDate() + 1);
        sDate.setHours(0, 0, 0, 0);
        eDate.setHours(0, 0, 0, 0);
        if (sDate.getTime() < eDate.getTime()) {
            timeDiff = (this.getTimeDifference(sDate, eDate, isCheckTimeZone)) / 1000;
        }
        if (timeDiff % 86400 !== 0) {
            timeDiff = timeDiff - (timeDiff % 86400) + 86400;
        }
        return timeDiff;
    }
    /**
     *
     * @param {Date} startDate .
     * @param {Date} endDate .
     * @param {CalendarContext} calendarContext .
     * @returns {number} .
     */
    getHolidaysCount(startDate, endDate, calendarContext) {
        let holidaysCount = 0;
        const holidays = calendarContext.defaultHolidays;
        const sDate = new Date(startDate.getTime());
        const eDate = new Date(endDate.getTime());
        sDate.setDate(sDate.getDate() + 1);
        sDate.setHours(0, 0, 0, 0);
        eDate.setHours(0, 0, 0, 0);
        if (sDate.getTime() < eDate.getTime()) {
            for (let i = 0; i < holidays.length; i++) {
                const currentHoliday = this.getDateFromFormat(new Date(holidays[i]));
                if (sDate.getTime() <= currentHoliday.getTime() && eDate.getTime() > currentHoliday.getTime()) {
                    if (!calendarContext.getExceptionForDate(currentHoliday)) {
                        if ((!this.parent.includeWeekend && this.parent.nonWorkingDayIndex.indexOf(currentHoliday.getDay()) === -1) ||
                            this.parent.includeWeekend) {
                            holidaysCount += 1;
                        }
                    }
                }
            }
        }
        return holidaysCount;
    }
    /**
     * @param {string} props .
     * @returns {number[]} .
     * @private
     */
    getHolidayDates(props) {
        if (props === 'holidays') {
            this.parent.calendarModule.holidays = this.parent.holidays;
        }
        else if (props === 'calendarSettings') {
            this.parent.calendarModule.holidays = this.parent.calendarSettings.projectCalendar.holidays;
        }
        const holidays = this.parent.calendarModule.holidays;
        const holidayDates = [];
        for (let i = 0; i < holidays.length; i++) {
            const from = this.getDateFromFormat(holidays[i].from);
            const to = this.getDateFromFormat(holidays[i].to);
            if (isNullOrUndefined(from) && isNullOrUndefined(to)) {
                continue;
            }
            else if (isNullOrUndefined(from) || isNullOrUndefined(to)) {
                const tempDate = from ? from : to;
                tempDate.setHours(0, 0, 0, 0);
                if (holidayDates.indexOf(tempDate.getTime()) === -1) {
                    holidayDates.push(tempDate.getTime());
                }
            }
            else {
                while (from <= to) {
                    from.setHours(0, 0, 0, 0);
                    if (holidayDates.indexOf(from.getTime()) === -1) {
                        holidayDates.push(from.getTime());
                    }
                    from.setDate(from.getDate() + 1);
                }
            }
        }
        return holidayDates;
    }
    /**
     * @param {Date} date .
     * @param {boolean} checkWeekEnd .
     * @param {CalendarContext} calendarContext .
     * @returns {boolean} .
     * @private
     */
    /*Check given date is on holidays*/
    isOnHolidayOrWeekEnd(date, checkWeekEnd, calendarContext) {
        const parent = this.parent;
        checkWeekEnd = !isNullOrUndefined(checkWeekEnd) ? checkWeekEnd : parent.includeWeekend;
        if (!parent.autoCalculateDateScheduling &&
            !(parent.isLoad && parent.treeGrid.loadChildOnDemand && parent.taskFields.hasChildMapping)) {
            checkWeekEnd = true;
        }
        if (calendarContext.getExceptionForDate(date)) {
            return false;
        }
        if (!checkWeekEnd && this.parent.nonWorkingDayIndex.indexOf(date.getDay()) !== -1) {
            return true;
        }
        const holidays = calendarContext.defaultHolidays;
        if (!holidays || holidays.length === 0) {
            return false;
        }
        const dateTime = date.getTime();
        for (let i = 0; i < holidays.length; i++) {
            const holidayDate = this.getDateFromFormat(new Date(holidays[i]));
            const holidayStart = holidayDate.setHours(0, 0, 0, 0);
            const holidayEnd = holidayDate.setHours(23, 59, 59, 999);
            if (dateTime >= holidayStart && dateTime <= holidayEnd) {
                return true;
            }
        }
        return false;
    }
    /**
     * To calculate non working times in given date
     *
     * @param {Date} startDate .
     * @param {Date} endDate .
     * @param {boolean} isAutoSchedule .
     * @param {boolean} isBaseline - Indicates whether the calculation is specific to baseline dates.
     * @param {CalendarContext} calendarContext .
     * @param {boolean} ignoreAutoSchedule - Indicates whether the calculation is specific to baseline dates.
     * @returns {number} .
     */
    getNonWorkingSecondsOnDate(startDate, endDate, isAutoSchedule, isBaseline, calendarContext, ignoreAutoSchedule) {
        const sHour = this.getSecondsInDecimal(startDate);
        const eHour = this.getSecondsInDecimal(endDate);
        let startRangeIndex = -1;
        let endRangeIndex = -1;
        let totNonWrkSecs = 0;
        const startOnHoliday = (isAutoSchedule && this.parent.autoCalculateDateScheduling &&
            !(this.parent.isLoad && this.parent.treeGrid.loadChildOnDemand && this.parent.taskFields.hasChildMapping)) && !isBaseline ? this.isOnHolidayOrWeekEnd(startDate, null, calendarContext) : false;
        const endOnHoliday = (isAutoSchedule && this.parent.autoCalculateDateScheduling &&
            !(this.parent.isLoad && this.parent.treeGrid.loadChildOnDemand && this.parent.taskFields.hasChildMapping)) && !isBaseline ? this.isOnHolidayOrWeekEnd(endDate, null, calendarContext) : false;
        let startnonWorkingTimeRange;
        let endnonWorkingTimeRange;
        if (this.parent.weekWorkingTime.length > 0) {
            startnonWorkingTimeRange = this.parent['getNonWorkingRange'](startDate);
            for (let i = 0; i < startnonWorkingTimeRange.length; i++) {
                const val = startnonWorkingTimeRange[i];
                if (sHour >= val.from && sHour <= val.to) {
                    startRangeIndex = i;
                }
            }
            endnonWorkingTimeRange = this.parent['getNonWorkingRange'](endDate);
            for (let i = 0; i < endnonWorkingTimeRange.length; i++) {
                const val = endnonWorkingTimeRange[i];
                if (eHour >= val.from && eHour <= val.to) {
                    endRangeIndex = i;
                }
            }
        }
        else {
            startnonWorkingTimeRange = this.parent.nonWorkingTimeRanges;
            endnonWorkingTimeRange = this.parent.nonWorkingTimeRanges;
            for (let i = 0; i < startnonWorkingTimeRange.length; i++) {
                const val = startnonWorkingTimeRange[i];
                if (sHour >= val.from && sHour <= val.to) {
                    startRangeIndex = i;
                }
                if (eHour >= val.from && eHour <= val.to) {
                    endRangeIndex = i;
                }
            }
        }
        if (startDate.getDate() !== endDate.getDate() || startDate.getMonth() !== endDate.getMonth() ||
            startDate.getFullYear() !== endDate.getFullYear()) {
            if (!startOnHoliday || ignoreAutoSchedule) {
                for (let i = startRangeIndex; i < startnonWorkingTimeRange.length; i++) {
                    if (!isNullOrUndefined(startnonWorkingTimeRange[i]) && !startnonWorkingTimeRange[i].isWorking) {
                        if (i === startRangeIndex) {
                            totNonWrkSecs += (startnonWorkingTimeRange[i].to - sHour);
                        }
                        else {
                            totNonWrkSecs += (startnonWorkingTimeRange[i].interval);
                        }
                    }
                }
            }
            else {
                totNonWrkSecs += (86400 - sHour);
            }
            if (!endOnHoliday || ignoreAutoSchedule) {
                for (let i = 0; i <= endRangeIndex; i++) {
                    if (!endnonWorkingTimeRange[i].isWorking) {
                        if (i === endRangeIndex) {
                            totNonWrkSecs += (eHour - endnonWorkingTimeRange[i].from);
                        }
                        else {
                            totNonWrkSecs += endnonWorkingTimeRange[i].interval;
                        }
                    }
                }
            }
            else {
                totNonWrkSecs += eHour;
            }
        }
        else {
            if (startRangeIndex !== endRangeIndex) {
                if (!endOnHoliday || ignoreAutoSchedule) {
                    for (let i = startRangeIndex; i <= endRangeIndex; i++) {
                        if (!isNullOrUndefined(startnonWorkingTimeRange[i]) && !startnonWorkingTimeRange[i].isWorking) {
                            if (i === startRangeIndex) {
                                totNonWrkSecs += (startnonWorkingTimeRange[i].to - sHour);
                            }
                            else if (i === endRangeIndex) {
                                totNonWrkSecs += (eHour - startnonWorkingTimeRange[i].from);
                            }
                            else {
                                totNonWrkSecs += startnonWorkingTimeRange[i].interval;
                            }
                        }
                    }
                }
                else {
                    totNonWrkSecs += (eHour - sHour);
                }
            }
            else {
                if (!endOnHoliday || ignoreAutoSchedule) {
                    const range = startnonWorkingTimeRange[startRangeIndex];
                    if (!range.isWorking) {
                        totNonWrkSecs = eHour - sHour;
                    }
                }
                else {
                    totNonWrkSecs += (eHour - sHour);
                }
            }
        }
        return totNonWrkSecs;
    }
    /**
     *
     * @param {Date} date .
     * @param {CalendarContext} calendarContext .
     * @returns {Date} .
     */
    getPreviousWorkingDay(date, calendarContext) {
        if (calendarContext) {
            const override = calendarContext.getExceptionForDate(date);
            if (override) {
                return date;
            }
        }
        const dayIndex = date.getDay();
        const previousIndex = (dayIndex === 0) ? 6 : dayIndex - 1;
        const dayEndTime = this.parent['getCurrentDayEndTime'](date);
        if (this.parent.nonWorkingDayIndex.indexOf(dayIndex) !== -1 || (this.parent.nonWorkingDayIndex.indexOf(previousIndex) !== -1
            && dayEndTime !== 86400 && this.getSecondsInDecimal(date) === 0)) {
            date.setDate(date.getDate() - 1);
            if (this.parent.nonWorkingDayIndex.indexOf(date.getDay()) !== -1) {
                date = this.getPreviousWorkingDay(date, calendarContext);
            }
            return date;
        }
        else {
            return date;
        }
    }
    /**
     * To get non-working day indexes.
     *
     * @returns {void} .
     * @private
     */
    getNonWorkingDayIndex() {
        const weekDay = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const weekDayLength = weekDay.length;
        if (this.parent.workWeek.length === 0) {
            this.parent.workWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
        }
        const workWeek = this.parent.workWeek.slice();
        const length = workWeek.length;
        for (let i = 0; i < length; i++) {
            workWeek[i] = workWeek[i].toLowerCase();
        }
        this.parent.nonWorkingDayIndex = [];
        for (let i = 0; i < weekDayLength; i++) {
            if (workWeek.indexOf(weekDay[i]) === -1) {
                this.parent.nonWorkingDayIndex.push(i);
            }
        }
    }
    /**
     *
     * @param {number} seconds .
     * @param {Date} date .
     * @returns {void} .
     * @private
     */
    setTime(seconds, date) {
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        let hour = seconds / 3600;
        hour = parseInt(hour, 10);
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        let min = (seconds - (hour * 3600)) / 60;
        min = parseInt(min, 10);
        const sec = seconds - (hour * 3600) - (min * 60);
        date.setHours(hour, min, sec);
    }
    /**
     * @param {Date} startDate .
     * @param {Date} endDate .
     * @param {boolean} isCheckTimeZone .
     * @returns {number} .
     */
    getTimeDifference(startDate, endDate, isCheckTimeZone) {
        const sDate = new Date(startDate.getTime());
        const eDate = new Date(endDate.getTime());
        if (isCheckTimeZone) {
            this.updateDateWithTimeZone(sDate, eDate);
        }
        return eDate.getTime() - sDate.getTime();
    }
    /**
     * @param {Date} sDate .
     * @param {Date} eDate .
     * @returns {void} .
     */
    updateDateWithTimeZone(sDate, eDate) {
        const sTZ = sDate.getTimezoneOffset();
        const eTZ = eDate.getTimezoneOffset();
        let uTZ;
        let uDate;
        if (sTZ !== eTZ) {
            const standardTZ = new Date(new Date().getFullYear(), 0, 1).getTimezoneOffset();
            if (standardTZ !== sTZ) {
                uDate = sDate;
                uTZ = sTZ;
            }
            else if (standardTZ !== eTZ) {
                uDate = eDate;
                uTZ = eTZ;
            }
            if (standardTZ < 0) {
                const tzDiff = standardTZ - uTZ;
                uDate.setTime(uDate.getTime() + (tzDiff * 60 * 1000));
            }
            else if (standardTZ >= 0) {
                const tzDiff = uTZ - standardTZ;
                uDate.setTime(uDate.getTime() - (tzDiff * 60 * 1000));
            }
        }
    }
    /**
     *
     * @param {Date} date .
     * @returns {number} .
     */
    getSecondsInDecimal(date) {
        return (date.getHours() * 60 * 60) + (date.getMinutes() * 60) + date.getSeconds() + (date.getMilliseconds() / 1000);
    }
    /**
     * @param {Date} date .
     * @param {number} localOffset .
     * @param {string} timezone .
     * @returns {number} .
     * @private
     */
    offset(date, timezone) {
        const localOffset = date.getTimezoneOffset();
        try {
            const convertedDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
            if (!isNaN(convertedDate.getTime())) {
                return ((date.getTime() - convertedDate.getTime()) / 60000) + localOffset;
            }
            return 0;
        }
        catch (error) {
            return 0;
        }
    }
    remove(date, timezone) {
        if (!isNullOrUndefined(date)) {
            date = this.reverse(date, timezone, date.getTimezoneOffset());
        }
        return date;
    }
    reverse(date, fromOffset, toOffset) {
        if (typeof fromOffset === 'string') {
            fromOffset = this.offset(date, fromOffset);
        }
        if (typeof toOffset === 'string') {
            toOffset = this.offset(date, toOffset);
        }
        const fromLocalOffset = date.getTimezoneOffset();
        date = new Date(date.getTime() + (fromOffset - toOffset) * 60000);
        const toLocalOffset = date.getTimezoneOffset();
        return new Date(date.getTime() + (toLocalOffset - fromLocalOffset) * 60000);
    }
    /**
     * @param {Date} date .
     * @param {string} timezone .
     * @returns {Date} .
     * @private
     */
    convert(date, timezone) {
        let fromOffset = date.getTimezoneOffset();
        let toOffset = this.offset(date, timezone);
        if (typeof fromOffset === 'string') {
            fromOffset = this.offset(date, fromOffset);
        }
        if (typeof toOffset === 'string') {
            toOffset = this.offset(date, toOffset);
        }
        const fromLocalOffset = date.getTimezoneOffset();
        date = new Date(date.getTime() + (fromOffset - toOffset) * 60000);
        const toLocalOffset = date.getTimezoneOffset();
        return new Date(date.getTime() + (toLocalOffset - fromLocalOffset) * 60000);
    }
    /**
     * @param {string | Date} date .
     * @param {boolean} toConvert .
     * @returns {Date} .
     * @private
     */
    getDateFromFormat(date, toConvert) {
        let updatedDate;
        if (isNullOrUndefined(date)) {
            return null;
        }
        else if (date instanceof Date) {
            updatedDate = new Date(date.getTime());
        }
        else {
            const dateObject = this.parent.globalize.parseDate(date, { format: this.parent.getDateFormat(), type: 'dateTime' });
            updatedDate = isNullOrUndefined(dateObject) && !isNaN(new Date(date).getTime()) ? new Date(date) : dateObject;
        }
        const updateTimezone = this.parent.isOnAdded ? false : true;
        if (!isNullOrUndefined(this.parent.timezone) && toConvert && ((updateTimezone && this.parent.timezone) || this.parent.isLoad)) {
            const convertedDate = this.convert(updatedDate, this.parent.timezone);
            return convertedDate;
        }
        else {
            return updatedDate;
        }
    }
    /**
     * @param {Date} date1 .
     * @param {Date} date2 .
     * @returns {number} .
     * @private
     */
    compareDates(date1, date2) {
        if (!isNullOrUndefined(date1) && !isNullOrUndefined(date2)) {
            return (date1.getTime() > date2.getTime()) ? 1 : (date1.getTime() < date2.getTime()) ? -1 : 0;
        }
        else if (!isNullOrUndefined(date1) && isNullOrUndefined(date2)) {
            return 1;
        }
        else if (isNullOrUndefined(date1) && !isNullOrUndefined(date2)) {
            return -1;
        }
        else {
            return null;
        }
    }
    /**
     *
     * @param {number} duration .
     * @param {string} durationUnit .
     * @returns {string} .
     * @private
     */
    getDurationString(duration, durationUnit) {
        let value = '';
        if (!isNullOrUndefined(duration)) {
            value += parseFloat(duration.toFixed(2)) + ' ';
            if (!isNullOrUndefined(durationUnit)) {
                const plural = duration !== 1;
                if (durationUnit === 'day') {
                    value += plural ? this.parent.localeObj.getConstant('days') : this.parent.localeObj.getConstant('day');
                }
                else if (durationUnit === 'hour') {
                    value += plural ? this.parent.localeObj.getConstant('hours') : this.parent.localeObj.getConstant('hour');
                }
                else if (durationUnit === 'minute') {
                    value += plural ? this.parent.localeObj.getConstant('minutes') :
                        this.parent.localeObj.getConstant('minute');
                }
            }
        }
        return value;
    }
    /**
     * Method to get work with value and unit.
     *
     * @param {number} work .
     * @param {string} workUnit .
     * @returns {string} .
     * @private
     */
    getWorkString(work, workUnit) {
        let value = '';
        if (!isNullOrUndefined(work)) {
            value += parseFloat(work).toFixed(2) + ' ';
            if (!isNullOrUndefined(workUnit)) {
                const plural = work !== 1;
                if (workUnit === 'day') {
                    value += plural ? this.parent.localeObj.getConstant('days') : this.parent.localeObj.getConstant('day');
                }
                else if (workUnit === 'hour') {
                    value += plural ? this.parent.localeObj.getConstant('hours') : this.parent.localeObj.getConstant('hour');
                }
                else if (workUnit === 'minute') {
                    value += plural ? this.parent.localeObj.getConstant('minutes') :
                        this.parent.localeObj.getConstant('minute');
                }
            }
        }
        return value;
    }
    /**
     *
     * @param {object} editArgs .
     * @returns {void} .
     * @private
     */
    // eslint-disable-next-line
    calculateProjectDatesForValidatedTasks(editArgs) {
        const projectStartDate = typeof this.parent.projectStartDate === 'string' ?
            new Date(this.parent.projectStartDate) : this.parent.projectStartDate;
        const projectEndDate = typeof this.parent.projectEndDate === 'string' ?
            new Date(this.parent.projectEndDate) : this.parent.projectEndDate;
        let minStartDate = null;
        let maxEndDate = null;
        const flatData = (getValue('dataOperation.dataArray', this.parent));
        if ((!projectStartDate || !projectEndDate) && (flatData && flatData.length === 0)) {
            minStartDate = this.getDateFromFormat(new Date());
            maxEndDate = this.getDateFromFormat(new Date(minStartDate.getTime()));
        }
        else if (flatData.length > 0) {
            const sortedStartDate = flatData.slice().sort((a, b) => ((new Date(a[this.parent.taskFields.startDate])).getTime() -
                (new Date(b[this.parent.taskFields.startDate])).getTime()));
            const sortedEndDate = flatData.slice().sort((a, b) => ((new Date(b[this.parent.taskFields.endDate])).getTime() - (new Date(a[this.parent.taskFields.endDate])).getTime()));
            minStartDate = sortedStartDate[0][this.parent.taskFields.startDate];
            maxEndDate = sortedEndDate[sortedEndDate.length - 1][this.parent.taskFields.endDate];
        }
        this.parent.cloneProjectStartDate = projectStartDate ? new Date(projectStartDate.getTime()) :
            typeof minStartDate === 'string' ? new Date(minStartDate) : minStartDate;
        this.parent.cloneProjectEndDate = projectEndDate ? new Date(projectEndDate.getTime()) :
            typeof maxEndDate === 'string' ? new Date(maxEndDate) : maxEndDate;
    }
    /**
     *
     * @param {object} editArgs .
     * @returns {void} .
     * @private
     */
    calculateProjectDates(editArgs) {
        if (this.parent.isLoad && this.parent.enablePersistence) {
            if (this.parent.cloneProjectStartDate && this.parent.cloneProjectEndDate) {
                this.parent.cloneProjectStartDate = this.getDateFromFormat(this.parent.cloneProjectStartDate);
                this.parent.cloneProjectEndDate = this.getDateFromFormat(this.parent.cloneProjectEndDate);
            }
            if (this.parent.cloneTimelineStartDate && this.parent.cloneTimelineEndDate) {
                this.parent.cloneTimelineStartDate = this.getDateFromFormat(this.parent.cloneTimelineStartDate);
                this.parent.cloneTimelineEndDate = this.getDateFromFormat(this.parent.cloneTimelineEndDate);
            }
            if ((this.parent.cloneProjectStartDate && this.parent.cloneProjectEndDate) ||
                (this.parent.cloneTimelineStartDate && this.parent.cloneTimelineEndDate)) {
                return;
            }
        }
        // Utility to safely convert string/Date/null to Date or null
        const toDate = (date) => date == null || (typeof date === 'string' && date.toLowerCase() === 'auto')
            ? null
            : (typeof date === 'string' ? new Date(date) : date);
        // Utility to resolve the correct date for project/timeline, considering zooming and clone values
        const resolveDate = (cloneDate, propDate) => this.getDateFromFormat(this.parent.timelineModule.isZooming && cloneDate ? cloneDate : propDate);
        const sDate = toDate(this.parent.projectStartDate);
        const eDate = toDate(this.parent.projectEndDate);
        const tSDate = toDate(this.parent.timelineSettings.viewStartDate);
        const tEDate = toDate(this.parent.timelineSettings.viewEndDate);
        const projectStartDate = resolveDate(this.parent.cloneProjectStartDate, sDate);
        const projectEndDate = resolveDate(this.parent.cloneProjectEndDate, eDate);
        const timelineStartDate = resolveDate(this.parent.cloneTimelineStartDate, tSDate);
        const timelineEndDate = resolveDate(this.parent.cloneTimelineEndDate, tEDate);
        let minStartDate = null;
        let maxEndDate = null;
        const flatData = this.parent.flatData;
        const currentViewData = this.parent.currentViewData;
        let taskRange = [];
        const addDateToList = (date) => {
            if (!isNullOrUndefined(date)) {
                taskRange.push(date);
            }
        };
        const sortDates = (dates) => {
            if (dates.length > 0) {
                dates.sort((a, b) => {
                    return a.getTime() - b.getTime();
                });
                minStartDate = new Date(dates[0].getTime());
                maxEndDate = dates.length > 1 ? new Date(dates[dates.length - 1].getTime()) : null;
            }
        };
        if ((flatData.length > 0) || editArgs || this.parent.timelineModule.isZoomToFit) {
            let viewData;
            if (currentViewData.length > 0 && this.parent.timelineModule.isZoomToFit &&
                this.parent.treeGrid.filterModule &&
                this.parent.treeGrid.filterModule.filteredResult.length > 0) {
                viewData = currentViewData;
            }
            else {
                viewData = flatData;
            }
            viewData.forEach((data) => {
                taskRange = [];
                const task = data.ganttProperties;
                let tempStartDate;
                let tempEndDate;
                if (isNullOrUndefined(task.startDate) && isNullOrUndefined(task.endDate)) {
                    tempStartDate = null;
                    tempEndDate = null;
                }
                else {
                    tempStartDate = this.getValidStartDate(task);
                    tempEndDate = this.getValidEndDate(task);
                }
                addDateToList(minStartDate);
                addDateToList(maxEndDate);
                addDateToList(tempStartDate);
                addDateToList(tempEndDate);
                if (this.parent.renderBaseline && !this.parent.timelineModule.isZoomToFit) {
                    addDateToList(task.baselineStartDate);
                    addDateToList(task.baselineEndDate);
                }
                if (task.indicators && task.indicators.length > 0 && !this.parent.timelineModule.isZoomToFit) {
                    task.indicators.forEach((item) => {
                        addDateToList(this.getDateFromFormat(item.date));
                    });
                }
                sortDates(taskRange);
            });
            taskRange = [];
            addDateToList(minStartDate);
            addDateToList(maxEndDate);
            //update schedule dates as per holiday and strip line collection
            if (this.parent.eventMarkers.length > 0 && !this.parent.timelineModule.isZoomToFit) {
                const eventMarkers = this.parent.eventMarkers;
                // eslint-disable-next-line
                eventMarkers.forEach((marker, index) => {
                    addDateToList(this.getDateFromFormat(marker.day));
                });
            }
            if (this.parent.defaultCalendarContext.defaultHolidays.length > 0 && !this.parent.timelineModule.isZoomToFit) {
                const holidays = this.parent.defaultCalendarContext.defaultHolidays;
                // eslint-disable-next-line
                holidays.forEach((holiday, index) => {
                    addDateToList(new Date(holiday));
                });
            }
            sortDates(taskRange);
            if (!minStartDate || !maxEndDate) {
                if (!minStartDate) {
                    minStartDate = isNullOrUndefined(minStartDate) ? this.getDateFromFormat(new Date()) : minStartDate;
                    minStartDate.setHours(0, 0, 0, 0);
                }
                else {
                    minStartDate = isNullOrUndefined(minStartDate) ? this.getDateFromFormat(new Date()) : minStartDate;
                }
                maxEndDate = this.getDateFromFormat(new Date(minStartDate.getTime()));
                maxEndDate.setDate(maxEndDate.getDate() + 20);
            }
        }
        else if (flatData.length === 0) {
            if (tSDate && !eDate) {
                minStartDate = this.getDateFromFormat(tSDate);
                maxEndDate = this.getDateFromFormat(new Date(minStartDate.getTime()));
            }
            else {
                minStartDate = this.getDateFromFormat(new Date());
                maxEndDate = this.getDateFromFormat(new Date(minStartDate.getTime()));
            }
        }
        if (!editArgs) {
            const getClonedDate = (zoomToFit, date, fallback) => !zoomToFit
                ? (!isNullOrUndefined(date) ? new Date(date.getTime()) : fallback)
                : fallback;
            this.prevProjectStartDate = this.parent.cloneProjectStartDate;
            const isZoomToFit = this.parent.timelineModule.isZoomToFit;
            this.parent.cloneProjectStartDate = getClonedDate(isZoomToFit, projectStartDate, minStartDate);
            this.parent.cloneProjectEndDate = getClonedDate(isZoomToFit, projectEndDate, maxEndDate);
            this.parent.cloneTimelineStartDate = getClonedDate(isZoomToFit, timelineStartDate, minStartDate);
            this.parent.cloneTimelineEndDate = this.parent.timelineSettings.viewStartDate !== 'auto' && this.parent.timelineSettings.viewEndDate === 'auto' ?
                this.parent.cloneProjectEndDate : getClonedDate(isZoomToFit, timelineEndDate, maxEndDate);
            if ((isNullOrUndefined(this.parent.projectEndDate) && this.parent.timelineSettings.viewEndDate === 'auto') ||
                (this.parent.timelineSettings.viewStartDate !== 'auto' && this.parent.timelineSettings.viewEndDate === 'auto')) {
                this.parent.timelineModule.adjustEndDateToFillChart(false);
            }
        }
        else {
            setValue('minStartDate', minStartDate, editArgs);
            setValue('maxEndDate', maxEndDate, editArgs);
        }
        this.parent['isProjectDateUpdated'] = true;
    }
    /**
     *
     * @param {ITaskSegment} segments .
     * @returns {number} .
     * @private
     */
    splitTasksDuration(segments) {
        let duration = 0;
        const sDate = segments[0].startDate;
        const eDate = segments[segments.length - 1].endDate;
        if (this.parent.timelineModule.bottomTier === 'Hour') {
            duration += Math.ceil(this.getTimeDifference(sDate, eDate) / (1000 * 60 * 60));
        }
        else if (this.parent.timelineModule.bottomTier === 'Minutes') {
            duration += Math.ceil(this.getTimeDifference(sDate, eDate) / (1000 * 60));
        }
        else {
            duration += Math.ceil(this.getTimeDifference(sDate, eDate) / (1000 * 60 * 60 * 24));
        }
        return duration;
    }
}
